import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React from "react";
import {
  Box,
  Button,
  Card,
  EmptyState,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  ScrollArea,
  SectionLabel,
  Separator,
  Skeleton,
  SkeletonGroup,
  Text,
  useIsMobile,
  useOptionalAutoFocus,
} from "@salestrak/ui";
import { useWorkspace, type Workspace } from "#app/lib/data/workspace-context";
import {
  filterSections,
  groupWorkspaces,
  isPendingInvitation,
  pickerWorkspaces,
  showsWorkspaceSearch,
  type WorkspaceSection,
} from "#app/lib/workspace-picker";
import { readWorkspaceRecents } from "#app/lib/workspace-recents";
import { useAuth } from "#app/lib/auth-context";
import { useAuthEmail } from "#app/lib/hooks/use-auth-email";
import { WorkspaceAvatar } from "#app/components/brand/workspace-avatar";
import { DateText } from "#app/components/date-text";
import { WorkspaceScreenLayout } from "#app/components/workspace-screen-layout";
import { daysLeftLabel } from "#app/lib/hooks/use-workspace-deletion";
import { routePath } from "#app/lib/hooks/use-app-navigate";
import { memberCountLabel } from "#app/lib/utils/member-count";
import { AnalyticsContext, WorkspaceEvent, trackEvent } from "#app/lib/analytics";

/** One row: a 32px tile beside a name over one subline. */
const ROW_HEIGHT = 52;

/**
 * The list scrolls inside its card past this height, so the search heading
 * the card holds still and the remainder fades out under it rather than the
 * page growing with the account. Desktop only: a phone has one scroller, the
 * page, because a card that scrolls inside a page that also scrolls is two
 * gestures on one thumb, and a card as tall as the viewport hides whatever
 * sits under it.
 */
const LIST_MAX_HEIGHT = 500;

/** The one door under the list, worded the same wherever it stands. */
const CREATE_LABEL = "Create new Workspace";

/**
 * The A–Z section's label when it leads the list: "Everything else" names
 * a remainder, and with no Invitations or Recent above it there is nothing
 * for it to be else than.
 */
function sectionLabel(section: WorkspaceSection, index: number): string {
  return section.key === "other" && index === 0 ? "Workspaces" : section.label;
}


/**
 * Whether a row opens something on press. Invites decide instead (Join /
 * Decline); expired and suspended rows have nothing to offer and list plain.
 * A frozen workspace still opens — onto the pending screen, where an Owner
 * can restore it.
 */
function isOpenable(workspace: Workspace): boolean {
  return workspace.memberStatus === "active";
}

function WorkspaceRow({
  workspace,
  section,
  openedAt,
  highlighted,
  onOpen,
  onHighlight,
  onAccept,
  onDecline,
}: {
  workspace: Workspace;
  section: WorkspaceSection["key"];
  /** When this device last opened the workspace; the Recent subline says so. */
  openedAt: string | undefined;
  /** The row Enter opens. */
  highlighted: boolean;
  onOpen: () => void;
  onHighlight: () => void;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const invited = isPendingInvitation(workspace);
  const frozen = section === "pendingDeletion";

  // The subline is the one fact that helps decide, and it differs by section:
  // an invite says who asked and when; a recent says when this device opened
  // it; a frozen row says when it goes; everything else says how big it is.
  let subline: React.ReactNode;
  if (invited) {
    subline = (
      <Box direction="row" align="center" gap="xs" minWidth={0}>
        <Text size="xs" variant="muted" truncate>
          {workspace.viewerInvitedBy?.name
            ? `Invited by ${workspace.viewerInvitedBy.name}`
            : "You've been invited"}
          {workspace.viewerInvitedAt ? " ·" : null}
        </Text>
        {workspace.viewerInvitedAt ? (
          <DateText date={workspace.viewerInvitedAt} format="long" size="xs" />
        ) : null}
      </Box>
    );
  } else if (frozen && workspace.deletion) {
    subline = (
      // In a row, not bare in the column: a bare block would stretch to the
      // row's width and the timestamp tooltip would center on the row, not
      // on the words.
      <Box direction="row" minWidth={0}>
        <DateText
          date={workspace.deletion.scheduledAt}
          label={`Deletes in ${daysLeftLabel(workspace.deletion.scheduledAt)}`}
          size="xs"
          color="orange"
        />
      </Box>
    );
  } else if (section === "recent" && openedAt) {
    subline = (
      <Box direction="row" align="center" gap="xs" minWidth={0}>
        <Text size="xs" variant="muted">
          Opened
        </Text>
        <DateText date={openedAt} format="long" size="xs" />
        <Text size="xs" variant="muted" truncate>
          · {memberCountLabel(workspace.memberCount)}
        </Text>
      </Box>
    );
  } else {
    subline = (
      <Text size="xs" variant="muted" truncate>
        {memberCountLabel(workspace.memberCount)}
      </Text>
    );
  }

  const identity = (
    <>
      {/* A frozen row's tile recedes with its name: the workspace is on its
          way out and the row is a door to restoring it, not to working in it. */}
      <Box opacity={frozen ? 70 : undefined} shrink={false}>
        <WorkspaceAvatar
          name={workspace.name}
          logo={workspace.settings?.logo}
          avatarColor={workspace.avatarColor}
          size="default"
        />
      </Box>
      <Box flex={1} gap="2xs" minWidth={0}>
        <Text size="sm" weight="medium" variant={frozen ? "muted" : undefined} truncate>
          {workspace.name}
        </Text>
        {subline}
      </Box>
    </>
  );

  if (isOpenable(workspace)) {
    return (
      <Box
        as="button"
        data-slot="workspace-row"
        // A press surface: a held finger washes the row and never selects
        // its name.
        group="row"
        fill="width"
        direction="row"
        align="center"
        gap="base"
        paddingX="base"
        height={ROW_HEIGHT}
        rounded="md"
        cursor="pointer"
        background={highlighted ? "selected" : undefined}
        hoverBackground={!highlighted}
        onClick={onOpen}
        onPointerEnter={onHighlight}
      >
        {identity}
      </Box>
    );
  }

  return (
    <Box
      data-slot="workspace-row"
      direction="row"
      align="center"
      gap="base"
      paddingX="base"
      height={ROW_HEIGHT}
    >
      {identity}
      {invited ? (
        // One verb: Join names the outcome and is the picker's own verb.
        // Decline stays reachable but demoted — the rare action — and a
        // declined invite keeps only Join.
        <Box direction="row" align="center" gap="xs" shrink={false}>
          {workspace.memberStatus === "invited" ? (
            <Button variant="ghost" shape="square" size="sm" onClick={onDecline}>
              Decline
            </Button>
          ) : null}
          <Button variant="outline" shape="square" size="sm" onClick={onAccept}>
            Join
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}

/** Creating is its own island under the list: the same row grammar (tile,
 * name, chevron) on its own card, so it reads as a door beside the list, not
 * a row in it. */
function CreateWorkspaceRow({ onClick }: { onClick: () => void }) {
  return (
    <Card>
      <Box
        as="button"
        onClick={onClick}
        direction="row"
        align="center"
        gap="base"
        paddingX="base"
        height={ROW_HEIGHT + 8}
        cursor="pointer"
        hoverBackground
      >
        <Box
          width={32}
          height={32}
          align="center"
          justify="center"
          rounded="md"
          background="muted"
        >
          <Icon name="plus" size="md" color="muted" />
        </Box>
        <Box flex={1}>
          <Text size="sm" weight="medium">
            {CREATE_LABEL}
          </Text>
        </Box>
        <Icon name="chevron-right" size="md" color="muted" />
      </Box>
    </Card>
  );
}

function WorkspaceSelectScreen() {
  const {
    workspaces,
    activeWorkspace,
    switchWorkspace,
    acceptInvitation,
    declineInvitation,
    isLoading,
  } = useWorkspace();

  const { signOut } = useAuth();
  const navigate = useNavigate();
  const userEmail = useAuthEmail();
  const isMobile = useIsMobile();

  // Recency is read once per visit: the list is grouped by it, and a stamp
  // written while this screen is open (there is none — opening a workspace
  // leaves it) would only regroup rows under the pointer.
  const [recents] = React.useState(readWorkspaceRecents);
  // The account list minus the workspaces the viewer left — every count and
  // row below reads this one, so a departed workspace neither shows nor
  // counts.
  const listed = React.useMemo(() => pickerWorkspaces(workspaces), [workspaces]);
  const sections = React.useMemo(
    () => groupWorkspaces(listed, recents),
    [listed, recents],
  );
  const hasSearch = showsWorkspaceSearch(listed.length);
  const [query, setQuery] = React.useState("");
  const visible = React.useMemo(
    () => (hasSearch ? filterSections(sections, query) : sections),
    [hasSearch, sections, query],
  );
  // Sections are a finding aid, so they arrive with the search: under the
  // threshold the list is short enough to read whole, and every row already
  // says what it is (an invite wears Join, a recent says when it opened, a
  // frozen one says when it goes) — labels and dividers around two names are
  // furniture. Past it, with one section left, its label would name the
  // whole list — noise.
  const sectioned = hasSearch && visible.length > 1;
  // The rows a press opens, in reading order: what ↑↓ walk and Enter takes.
  const openable = React.useMemo(
    () => visible.flatMap((s) => s.workspaces.filter(isOpenable)),
    [visible],
  );
  const [highlightedId, setHighlightedId] = React.useState<string | null>(null);
  // The highlight always names a row that exists: the first openable one
  // until the reader moves it, and back to the first when a query drops the
  // row it sat on.
  const highlightIndex = Math.max(
    0,
    openable.findIndex((w) => w.workspaceId === highlightedId),
  );
  const highlighted = openable[highlightIndex];

  const searchRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  // The search heads a list where typing is optional, so the caret seats only
  // where a hardware keyboard is implied.
  const autoFocus = useOptionalAutoFocus();

  const openWorkspace = React.useCallback(
    async (ws: Workspace) => {
      const switched = await switchWorkspace(ws.workspaceId);
      if (switched !== false) {
        trackEvent({
          context: AnalyticsContext.Workspace,
          event: WorkspaceEvent.Switch,
          data: { via: "picker" },
        });
      }
      navigate({ to: "/", replace: true });
    },
    [switchWorkspace, navigate],
  );

  const moveHighlight = (delta: number) => {
    const next = openable[(highlightIndex + delta + openable.length) % openable.length];
    if (!next) return;
    setHighlightedId(next.workspaceId);
    listRef.current
      ?.querySelector(`[data-workspace-id="${next.workspaceId}"]`)
      ?.scrollIntoView({ block: "nearest" });
  };

  // `/` returns focus to the search from anywhere on the page that is not
  // already a field — the one search this screen has, so it owns the key.
  React.useEffect(() => {
    if (!hasSearch) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target && target.closest("input, textarea, [contenteditable=true]")) return;
      event.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasSearch]);

  const title =
    isLoading || listed.length > 0
      ? "Choose a Workspace"
      : "Create a Workspace to get started";

  // The picker's own shape while the list loads: the heading renders real
  // and a card of three row stand-ins (tile, name and subline bars) holds
  // where the workspaces will land. Delayed as one.
  if (isLoading) {
    return (
      <WorkspaceScreenLayout
        anchor="top"
        email={userEmail}
        onBack={signOut}
        backLabel="Back to login"
        backSignsOut
      >
        <Box paddingBottom="xl">
          <Text as="h3" size="xl" weight="medium" align="center">
            {title}
          </Text>
        </Box>
        <SkeletonGroup aria-hidden>
          <Card>
            <Box paddingX="sm" paddingY="sm">
              {[0, 1, 2].map((index) => (
                <Box
                  key={index}
                  direction="row"
                  align="center"
                  gap="base"
                  paddingX="base"
                  height={ROW_HEIGHT}
                >
                  <Skeleton width={32} height={32} className="rounded-md" />
                  <Box flex={1} gap="xs">
                    <Skeleton height={12} width={[132, 96, 156][index]} />
                    <Skeleton height={10} width={64} />
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        </SkeletonGroup>
      </WorkspaceScreenLayout>
    );
  }

  return (
    <WorkspaceScreenLayout
      anchor="top"
      email={userEmail}
      // Reached from inside a workspace (the selector's "Create or Join"),
      // back returns to it; with nothing to return to, back is the login
      // screen, which means signing out.
      onBack={
        activeWorkspace
          ? () =>
              navigate({
                to: routePath(`/${activeWorkspace.urlPath}`),
              })
          : signOut
      }
      backLabel={activeWorkspace ? "Back to SalesTrak" : "Back to login"}
      backSignsOut={!activeWorkspace}
      // On a phone the door is pinned under the list, where it stays in
      // reach however long the account is; on a desktop the capped card
      // leaves it in view as its own island below.
      footer={
        isMobile ? (
          <Button
            variant="outline"
            shape="square"
            size="xl"
            fullWidth
            onClick={() => navigate({ to: "/workspace-create" })}
          >
            {CREATE_LABEL}
          </Button>
        ) : undefined
      }
    >
      {/* Header rhythm matches workspace-create: xl/medium heading, 32px
          to the content below. */}
      <Box paddingBottom="xl">
        <Text as="h3" size="xl" weight="medium" align="center">
          {title}
        </Text>
      </Box>

      <Box gap="md">
        {listed.length > 0 && (
          // One card: the search is its head and the list scrolls beneath it,
          // so the field the reader types in never moves.
          <Card direction="column" maxHeight={isMobile ? undefined : LIST_MAX_HEIGHT + 68}>
            {hasSearch ? (
              // 8px inset all round; the first section label's own 12px top
              // pad completes the 20px between the well and the label.
              <Box padding="sm" shrink={false}>
                <InputGroup variant="outline">
                  <InputGroupAddon>
                    <Icon name="search" size="md" color="muted" />
                  </InputGroupAddon>
                  <InputGroupInput
                    ref={searchRef}
                    size="xl"
                    autoFocus={autoFocus}
                    placeholder={`Search ${listed.length} workspaces`}
                    aria-label="Search workspaces"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Escape" && query) {
                        event.preventDefault();
                        setQuery("");
                      } else if (event.key === "ArrowDown") {
                        event.preventDefault();
                        moveHighlight(1);
                      } else if (event.key === "ArrowUp") {
                        event.preventDefault();
                        moveHighlight(-1);
                      } else if (event.key === "Enter" && highlighted) {
                        event.preventDefault();
                        void openWorkspace(highlighted);
                      }
                    }}
                  />
                  {query ? (
                    <InputGroupAddon align="inline-end">
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Clear search"
                        onClick={() => {
                          setQuery("");
                          searchRef.current?.focus();
                        }}
                      >
                        <Icon name="x" size="sm" />
                      </Button>
                    </InputGroupAddon>
                  ) : null}
                </InputGroup>
              </Box>
            ) : null}
            {/* scrollFade: a long list dissolves at the card's edges instead
                of cutting on a row. */}
            <ScrollArea scrollFade>
              {/* Without the search head the rows are the card's first
                  content, so the list wears the head's 8px inset itself. */}
              <Box
                ref={listRef}
                paddingX="sm"
                paddingTop={hasSearch ? undefined : "sm"}
                paddingBottom="sm"
              >
                {visible.length === 0 ? (
                  <Box padding="lg">
                    <EmptyState
                      size="sm"
                      icon="search"
                      title="No workspaces match"
                      description={`Nothing you belong to is named “${query.trim()}”.`}
                    />
                  </Box>
                ) : null}
                {visible.map((section, sectionIndex) => (
                  <Box key={section.key}>
                    {sectioned && sectionIndex > 0 ? (
                      <Box paddingX="xs" paddingTop="sm">
                        <Separator />
                      </Box>
                    ) : null}
                    {sectioned ? (
                      <Box paddingBottom="xs" paddingTop="base" paddingX="base">
                        <SectionLabel>{sectionLabel(section, sectionIndex)}</SectionLabel>
                      </Box>
                    ) : null}
                    {section.workspaces.map((ws) => (
                      <Box key={ws.workspaceId} data-workspace-id={ws.workspaceId}>
                        <WorkspaceRow
                          workspace={ws}
                          section={section.key}
                          openedAt={recents[ws.workspaceId]}
                          // The highlight is the row Enter takes from the
                          // search; with no search, or on a touch device
                          // where the search seats no caret, there is no
                          // Enter to promise, and the wash would sit on the
                          // first row (or the last row tapped) for no reason.
                          highlighted={
                            hasSearch && autoFocus && highlighted?.workspaceId === ws.workspaceId
                          }
                          onOpen={() => void openWorkspace(ws)}
                          onHighlight={() => setHighlightedId(ws.workspaceId)}
                          onAccept={async () => {
                            await acceptInvitation(ws.workspaceId, "picker");
                            navigate({ to: "/", replace: true });
                          }}
                          onDecline={() => declineInvitation(ws.workspaceId, "picker")}
                        />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </ScrollArea>
          </Card>
        )}

        {isMobile ? null : (
          <CreateWorkspaceRow onClick={() => navigate({ to: "/workspace-create" })} />
        )}
      </Box>
    </WorkspaceScreenLayout>
  );
}

// The lobby: signed in, but not yet in a space whose look applies.
export const Route = createFileRoute("/(app)/workspace-select")({
  component: WorkspaceSelectScreen,
  staticData: { standardAppearance: true },
});
